import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../api";
import { date } from "../../format";
import { Alert, Card, Field } from "../../shell/ui";

interface Season {
  id: string;
  title: string;
  description: string;
  starts_at: string;
  ends_at: string;
  kind: "monthly" | "custom";
  phase: "upcoming" | "running" | "finished";
  final: boolean;
  participants?: number;
}

/** A datetime-local value `days` from now, at the top of the hour. */
function inDays(days: number): string {
  const d = new Date(Date.now() + days * 86_400_000);
  d.setMinutes(0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:00`;
}

const PRESETS = [
  { key: "day", label: "Daily sprint", hint: "today, 24 hours" },
  { key: "weekend", label: "Weekend sprint", hint: "Friday to Sunday" },
  { key: "week", label: "Weekly cup", hint: "Monday to Sunday" },
] as const;

/**
 * The league runs itself: one season per month, every public bot in it. Here an admin
 * can see the seasons and add a special one: one click for a ready-made one (name,
 * description and dates filled in), or two dates; a name and description are optional.
 */
export function LeagueTab() {
  const qc = useQueryClient();
  const seasons = useQuery({
    queryKey: ["league", "admin"],
    queryFn: () => api<{ items: Season[] }>("/hub/league/seasons"),
  });
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startsAt, setStartsAt] = useState(inDays(1));
  const [endsAt, setEndsAt] = useState(inDays(8));
  const refresh = () => qc.invalidateQueries({ queryKey: ["league"] });
  const create = useMutation({
    mutationFn: (body: Record<string, string>) =>
      api<{ id: string; title: string }>("/hub/admin/league/seasons", { method: "POST", body }),
    onSuccess: () => {
      setTitle("");
      setDescription("");
      void refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/hub/admin/league/seasons/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
  });
  const error = create.error ?? remove.error;

  return (
    <div className="stack">
      <Card title="Seasons" flush>
        <p className="card__body small muted">
          Automatic: a season every calendar month (UTC), and every public paper bot takes part. Finished
          seasons are frozen within the hour after they end, and each owner is told where their best bot
          finished.
        </p>
        {error && <Alert tone="error">{error.message}</Alert>}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Season</th>
                <th>Kind</th>
                <th>Phase</th>
                <th>From</th>
                <th>Until</th>
                <th className="num">Bots</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(seasons.data?.items ?? []).map((s) => (
                <tr key={s.id}>
                  <td>
                    <Link to={`/league/${s.id}`}>{s.title}</Link>
                    {s.description && <div className="small muted">{s.description}</div>}
                  </td>
                  <td className="small">{s.kind}</td>
                  <td className="small">
                    {s.phase}
                    {s.final && " · final"}
                  </td>
                  <td className="small">{date(s.starts_at, true)}</td>
                  <td className="small">{date(s.ends_at, true)}</td>
                  <td className="num">{s.participants ?? 0}</td>
                  <td>
                    {s.kind === "custom" && !s.final && (
                      <button
                        type="button"
                        className="btn btn--sm btn--danger"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(s.id)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Add a season">
        <div className="stack">
          <div className="row">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                className="btn btn--primary"
                disabled={create.isPending}
                title={p.hint}
                onClick={() => create.mutate({ preset: p.key })}
              >
                {p.label}
              </button>
            ))}
          </div>
          <p className="small muted">
            One click: name, description and dates are filled in ({PRESETS.map((p) => p.hint).join("; ")}).
            Every public bot is in it automatically.
          </p>
          {create.isSuccess && (
            <p className="small">
              Added <Link to={`/league/${create.data.id}`}>{create.data.title}</Link>.
            </p>
          )}
          <details>
            <summary className="small">Pick your own dates</summary>
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                create.mutate({
                  title,
                  description,
                  startsAt: new Date(startsAt).toISOString(),
                  endsAt: new Date(endsAt).toISOString(),
                });
              }}
            >
              <div className="form-grid">
                <Field label="From">
                  <input
                    type="datetime-local"
                    value={startsAt}
                    onChange={(e) => setStartsAt(e.target.value)}
                    required
                  />
                </Field>
                <Field label="Until">
                  <input
                    type="datetime-local"
                    value={endsAt}
                    onChange={(e) => setEndsAt(e.target.value)}
                    required
                  />
                </Field>
                <Field label="Name (optional)">
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Sprint · 6–12 Oct"
                    maxLength={80}
                  />
                </Field>
                <Field label="Description (optional)">
                  <input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Every public bot, ranked on its live results…"
                    maxLength={280}
                  />
                </Field>
              </div>
              <div className="row">
                <button type="submit" className="btn" disabled={create.isPending}>
                  Add season
                </button>
              </div>
            </form>
          </details>
        </div>
      </Card>
    </div>
  );
}
